// Aperçu en deux onglets (05/10) : lit le texte extrait d'un document depuis la source choisie par
// sourceDuTexteExtrait (lib/compta/texte_extrait.ts). N'est monté qu'à l'ouverture de l'onglet
// « Texte extrait » : l'onglet « Original » ne déclenche aucune lecture du texte.
import type { SourceTexteExtrait } from "@app/lib/compta/texte_extrait";
import { estTexteMarkdown } from "@app/lib/compta/texte_extrait";
import { useDataSourceViewDocument } from "@app/lib/swr/data_source_view_documents";
import { useFileContentByUrl } from "@app/lib/swr/files";
import type { LightWorkspaceType } from "@app/types/user";

export type EtatTexteExtrait =
  | { etat: "chargement" }
  | { etat: "erreur" }
  | { etat: "pret"; texte: string; markdown: boolean };

/** Version traitée (texte extrait à l'envoi) d'un fichier du workspace. */
export function adresseTexteTraite(
  owner: Pick<LightWorkspaceType, "sId">,
  fileId: string
): string {
  return `/api/w/${owner.sId}/files/${encodeURIComponent(fileId)}?action=view&version=processed`;
}

export function useTexteExtrait({
  owner,
  source,
}: {
  owner: LightWorkspaceType;
  source: SourceTexteExtrait;
}): EtatTexteExtrait {
  const fichier = source.type === "fichier" ? source : null;
  const document = source.type === "document" ? source : null;

  const { fileContent, isNotFound, isFileContentLoading, fileContentError } =
    useFileContentByUrl({
      url: fichier ? adresseTexteTraite(owner, fichier.fileId) : null,
      disabled: !fichier,
    });

  const {
    document: documentIndexe,
    isDocumentLoading,
    isDocumentError,
  } = useDataSourceViewDocument({
    dataSourceView: document?.dataSourceView ?? null,
    documentId: document?.documentId ?? null,
    owner,
    disabled: !document,
  });

  if (fichier) {
    if (fileContentError) {
      return { etat: "erreur" };
    }
    if (isNotFound) {
      return { etat: "pret", texte: "", markdown: false };
    }
    if (isFileContentLoading || fileContent === null) {
      return { etat: "chargement" };
    }
    return { etat: "pret", texte: fileContent, markdown: false };
  }

  if (isDocumentError) {
    return { etat: "erreur" };
  }
  if (isDocumentLoading || !documentIndexe) {
    return { etat: "chargement" };
  }
  return {
    etat: "pret",
    texte: documentIndexe.text ?? "",
    markdown: estTexteMarkdown(documentIndexe.mime_type),
  };
}
